import React, { useMemo } from "react";
import { connect } from "react-redux";

import { MessageDescriptor } from "@formatjs/intl/src/types";
import { defineMessages, useIntl } from "react-intl";

import CountryCode from "@mapmycustomers/shared/enum/CountryCode";
import TerritoryLevel from "@mapmycustomers/shared/enum/TerritoryLevel";
import Currency from "@mapmycustomers/shared/types/Currency";
import AreaRegion from "@mapmycustomers/shared/types/territory/AreaRegion";
import AreaType from "@mapmycustomers/shared/types/territory/AreaType";
import LookupData from "@mapmycustomers/shared/types/territory/LookupData";
import { parseAreaType } from "@mapmycustomers/shared/util/territory/areaTypes";

import OverlayView from "@app/component/map/FeaturedMap/essentials/OverlayView";
import useMap from "@app/component/map/FeaturedMap/utils/useMap";
import DemographicData from "@app/scene/map/types/DemographicData";
import { getCurrencies } from "@app/store/referenceData";
import { RootState } from "@app/store/rootReducer";
import { BASE_TOOLTIP_ZINDEX } from "@app/util/map/consts";

import { ContinuousSelectionMode } from "../../utils/useContinuousSelectionMode";

import styles from "./RegionTooltip.module.scss";

const OFFSET = { x: -120, y: 20 };

const messages = defineMessages({
  baseTooltip: {
    id: "map.feature.areaRegion.hover.tooltip.base",
    defaultMessage: "{selected, select, true {Exclude ''{name}''} other {Include ''{name}''}}",
  },
  country: {
    id: "map.feature.areaRegion.hover.tooltip.nonNative.regionType.country",
    defaultMessage: "countries",
  },
  county: {
    id: "map.feature.areaRegion.hover.tooltip.nonNative.regionType.county",
    defaultMessage: "counties",
  },
  defaultRegionName: {
    id: "map.feature.areaRegion.hover.tooltip.defaultName",
    defaultMessage: "Region",
  },
  district: {
    id: "map.feature.areaRegion.hover.tooltip.nonNative.regionType.district",
    defaultMessage: "districts",
  },
  division: {
    id: "map.feature.areaRegion.hover.tooltip.nonNative.regionType.division",
    defaultMessage: "divisions",
  },
  localGovernmentArea: {
    id: "map.feature.areaRegion.hover.tooltip.nonNative.regionType.localGovernmentArea",
    defaultMessage: "local government areas",
  },
  medianIncome: {
    id: "map.feature.areaRegion.hover.tooltip.medianIncome",
    defaultMessage: "Median Income",
    description: "Median Income tooltip title",
  },
  nonNativeLevelMessage: {
    id: "map.feature.areaRegion.hover.tooltip.nonNative",
    defaultMessage:
      "{include, select, true {Include} other {Exclude}}{remaining, select, true { remaining} other {}} {regionType} within ''{name}''",
  },
  population: {
    id: "map.feature.areaRegion.hover.tooltip.population",
    defaultMessage: "Population",
    description: "Population tooltip title",
  },
  postalCode: {
    id: "map.feature.areaRegion.hover.tooltip.nonNative.regionType.postalCode",
    defaultMessage: "zip codes",
  },
  province: {
    id: "map.feature.areaRegion.hover.tooltip.nonNative.regionType.province",
    defaultMessage: "provinces",
  },
  state: {
    id: "map.feature.areaRegion.hover.tooltip.nonNative.regionType.state",
    defaultMessage: "states",
  },
});

const areaTypeToBaseRegionType: Record<AreaType, MessageDescriptor> = {
  [`${CountryCode["AUSTRALIA"]}_${TerritoryLevel.ADM1}` as AreaType]: messages.state,
  [`${CountryCode["AUSTRALIA"]}_${TerritoryLevel.ADM2}` as AreaType]: messages.localGovernmentArea,
  [`${CountryCode["AUSTRALIA"]}_${TerritoryLevel.ADM3}` as AreaType]: messages.postalCode,
  [`${CountryCode["CANADA"]}_${TerritoryLevel.ADM1}` as AreaType]: messages.province,
  [`${CountryCode["CANADA"]}_${TerritoryLevel.ADM2}` as AreaType]: messages.division,
  [`${CountryCode["CANADA"]}_${TerritoryLevel.ADM3}` as AreaType]: messages.postalCode,
  [`${CountryCode["UNITED KINGDOM OF GREAT BRITAIN AND NORTHERN IRELAND"]}_${TerritoryLevel.ADM1}` as AreaType]:
    messages.country,
  [`${CountryCode["UNITED KINGDOM OF GREAT BRITAIN AND NORTHERN IRELAND"]}_${TerritoryLevel.ADM2}` as AreaType]:
    messages.district,
  [`${CountryCode["UNITED KINGDOM OF GREAT BRITAIN AND NORTHERN IRELAND"]}_${TerritoryLevel.ADM3}` as AreaType]:
    messages.postalCode,
  [`${CountryCode["UNITED STATES OF AMERICA"]}_${TerritoryLevel.ADM1}` as AreaType]: messages.state,
  [`${CountryCode["UNITED STATES OF AMERICA"]}_${TerritoryLevel.ADM2}` as AreaType]:
    messages.county,
  [`${CountryCode["UNITED STATES OF AMERICA"]}_${TerritoryLevel.ADM3}` as AreaType]:
    messages.postalCode,
};

interface Props<T> {
  areaType: AreaType;
  continuousSelectionMode: ContinuousSelectionMode;
  currencies: Currency[];
  currentLevel: TerritoryLevel;
  lookupData: LookupData;
  position: google.maps.LatLngLiteral | undefined;
  region?: T;
  regionDemographyData?: Omit<DemographicData, "code" | "type">;
  selectedRegionIds?: AreaRegion["id"][];
}

const RegionTooltip = <T extends AreaRegion>({
  areaType,
  continuousSelectionMode,
  currencies,
  currentLevel,
  lookupData,
  position,
  region,
  regionDemographyData,
  selectedRegionIds,
}: Props<T>) => {
  const intl = useIntl();
  const { map } = useMap();

  const isSelected = region && selectedRegionIds?.includes(region.id);
  const { level } = parseAreaType(areaType)!;
  const isNonNativeLevel = level !== currentLevel;
  const selectionType: "full" | "none" | "partial" = useMemo(() => {
    if (!isNonNativeLevel || !selectedRegionIds?.length || !region?.id) {
      return "none";
    }
    const expectedRegionIds: AreaRegion["id"][] =
      // We're safe here because currentLevel can never be ADM3 at this point.
      // @ts-ignore
      lookupData[currentLevel][region.id]?.[level] ?? [];
    const currentSelectionSet = new Set(selectedRegionIds);
    const matches = expectedRegionIds.filter((regionId) => currentSelectionSet.has(regionId));
    return !matches.length
      ? "none"
      : matches.length === expectedRegionIds.length
      ? "full"
      : "partial";
  }, [currentLevel, isNonNativeLevel, level, lookupData, region?.id, selectedRegionIds]);

  const message = useMemo(() => {
    // nothing selected
    if (!region) {
      return undefined;
    }

    const regionName = region.name || intl.formatMessage(messages.defaultRegionName);

    if (continuousSelectionMode !== "none") {
      // just show the name in continuous selection mode
      return regionName;
    }

    if (!isNonNativeLevel) {
      return intl.formatMessage(messages.baseTooltip, {
        name: regionName,
        selected: isSelected,
      });
    }

    return intl.formatMessage(messages.nonNativeLevelMessage, {
      include: selectionType !== "full",
      name: regionName,
      regionType: intl.formatMessage(areaTypeToBaseRegionType[areaType]),
      remaining: selectionType === "partial",
    });
  }, [
    areaType,
    continuousSelectionMode,
    intl,
    isNonNativeLevel,
    selectionType,
    isSelected,
    region,
  ]);

  const currency = currencies?.find(
    (currency) => currency.id === regionDemographyData?.income?.currencyId
  );
  const isVisible = map && position && message;

  return isVisible ? (
    <OverlayView
      map={map}
      offset={OFFSET}
      pane="markerLayer"
      position={position}
      zIndex={BASE_TOOLTIP_ZINDEX}
    >
      <div className={styles.container}>
        <div className={styles.text}>{message}</div>
        {regionDemographyData && (
          <div className={styles.infoArea}>
            <div className={styles.dataRow}>
              <span>{intl.formatMessage(messages.medianIncome)}</span>
              <span className={styles.data}>
                {regionDemographyData.income.value != null
                  ? intl.formatNumber(regionDemographyData.income.value, {
                      currency: currency?.code,
                      minimumFractionDigits: 0,
                      style: "currency",
                    })
                  : ""}
              </span>
            </div>
            <div className={styles.dataRow}>
              <span>{intl.formatMessage(messages.population)}</span>
              <span className={styles.data}>
                {intl.formatNumber(regionDemographyData.population)}
              </span>
            </div>
          </div>
        )}
      </div>
    </OverlayView>
  ) : null;
};

const mapStateToProps = (state: RootState) => ({
  currencies: getCurrencies(state),
});

export default connect(mapStateToProps)(RegionTooltip);
